import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type AdminTournament } from '@/api/admin';
import { CasinoButton } from '@/components/CasinoButton';
import { SignPanel } from '@/components/SignPanel';
import { useAdminPendingSummary } from '@/hooks/useAdminPendingSummary';
import { cn } from '@/lib/utils';
import { formatDollars } from '@/lib/payouts';
import { AdminHeader } from './TournamentNew';

const statusStyles: Record<string, string> = {
  setup: 'bg-cabinet-cream/10 text-cabinet-cream',
  active: 'bg-cabinet-gold text-cabinet-purple-ink',
  completed: 'bg-cabinet-flame-orange/30 text-cabinet-flame-orange',
  archived: 'bg-cabinet-cream/5 text-cabinet-cream/40',
};

export function AdminHome() {
  const q = useQuery({
    queryKey: ['admin', 'tournaments'],
    queryFn: adminApi.listTournaments,
  });
  const qc = useQueryClient();
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const deleteM = useMutation({
    mutationFn: (id: number) => adminApi.deleteTournament(id),
    onSuccess: async () => {
      setPendingDeleteId(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'tournaments'] });
    },
    onError: (err) => {
      const e = err as { reason?: string };
      setError(
        e.reason === 'has_entries'
          ? 'Cannot delete — entries exist.'
          : e.reason === 'not_in_setup'
            ? 'Cannot delete — tournament has started.'
            : 'Could not delete.',
      );
    },
  });

  const tournaments = q.data?.tournaments ?? [];
  const active = tournaments.find((t) => t.status === 'active');
  const pendingTarget = tournaments.find((t) => t.id === pendingDeleteId);

  const summaryQ = useAdminPendingSummary();
  const summary = summaryQ.data;
  const manageEntriesTo = summary?.activeTournamentId
    ? `/admin/tournament/${summary.activeTournamentId}/manage#entries`
    : active
      ? `/admin/tournament/${active.id}/manage#entries`
      : null;

  return (
    <main className="mx-auto max-w-3xl px-5 py-8 space-y-6">
      <AdminHeader title="Admin" crumbs={[{ label: 'Admin' }]} />

      <div className="flex flex-wrap gap-3">
        <Link to="/admin/tournament/new">
          <CasinoButton variant="gold">New tournament</CasinoButton>
        </Link>
        <Link to="/admin/users">
          <CasinoButton variant="ghost">Users</CasinoButton>
        </Link>
        <Link to="/admin/export">
          <CasinoButton variant="ghost">Export CSV</CasinoButton>
        </Link>
      </div>

      <section aria-label="Needs attention" className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <NeedsAttentionCard
          label="Pending user approvals"
          count={summary?.pendingUsers ?? 0}
          to="/admin/users"
        />
        <NeedsAttentionCard
          label="Unverified entries"
          count={summary?.unverifiedEntries ?? 0}
          to={manageEntriesTo}
        />
        <NeedsAttentionCard
          label="Unpaid entries"
          count={summary?.unpaidEntries ?? 0}
          to={manageEntriesTo}
        />
      </section>

      {active && (
        <section className="cabinet-card p-5">
          <p className="text-xs uppercase tracking-widest text-cabinet-gold-bright">
            Active
          </p>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-4">
            <SignPanel size="md">
              <h2 className="text-marquee text-2xl uppercase leading-none">
                {active.name}
              </h2>
            </SignPanel>
            <Link
              to={`/admin/tournament/${active.id}/manage`}
              className="text-cabinet-gold underline-offset-2 hover:underline text-sm"
            >
              Manage →
            </Link>
          </div>
        </section>
      )}

      <section className="cabinet-card p-5 space-y-3">
        <h3 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
          All tournaments
        </h3>
        {q.isLoading ? (
          <p className="text-sm text-cabinet-cream/60">Loading…</p>
        ) : tournaments.length === 0 ? (
          <p className="text-sm text-cabinet-cream/60">
            No tournaments yet. Create your first one above.
          </p>
        ) : (
          <ul className="space-y-2">
            {tournaments.map((t) => (
              <TournamentRow
                key={t.id}
                t={t}
                onRequestDelete={(id) => {
                  setError(null);
                  setPendingDeleteId(id);
                }}
              />
            ))}
          </ul>
        )}
        {error && (
          <p className="text-sm text-cabinet-flame-orange">{error}</p>
        )}
      </section>

      {pendingTarget && (
        <DeleteConfirm
          name={pendingTarget.name}
          pending={deleteM.isPending}
          onConfirm={() => deleteM.mutate(pendingTarget.id)}
          onCancel={() => setPendingDeleteId(null)}
        />
      )}
    </main>
  );
}

function NeedsAttentionCard({
  label,
  count,
  to,
}: {
  label: string;
  count: number;
  /** Destination for the action link. Null = no actionable target (e.g. no active tournament). */
  to: string | null;
}) {
  const hot = count > 0;
  const body = (
    <div
      className={cn(
        'cabinet-card p-4 h-full flex flex-col gap-1 transition',
        hot
          ? 'border-cabinet-flame-orange/60 hover:border-cabinet-flame-orange'
          : 'opacity-70',
      )}
    >
      <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">
        {label}
      </p>
      <p
        className={cn(
          'font-display text-3xl tabular-nums leading-none',
          hot ? 'text-cabinet-flame-orange' : 'text-cabinet-cream/40',
        )}
      >
        {count}
      </p>
      {hot && to && (
        <p className="mt-auto pt-1 text-xs text-cabinet-gold underline-offset-2 group-hover:underline">
          Take action →
        </p>
      )}
    </div>
  );
  if (hot && to) {
    return (
      <Link to={to} className="group block">
        {body}
      </Link>
    );
  }
  return body;
}

function TournamentRow({
  t,
  onRequestDelete,
}: {
  t: AdminTournament;
  onRequestDelete: (id: number) => void;
}) {
  const canDelete = t.status === 'setup';
  return (
    <li className="flex items-center gap-3 rounded-md ring-1 ring-cabinet-cream/10 bg-cabinet-purple-ink/40 hover:ring-cabinet-gold/50 px-3 py-2 transition group">
      <Link
        to={`/admin/tournament/${t.id}/manage`}
        className="flex items-center gap-3 flex-1 min-w-0"
      >
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold',
            statusStyles[t.status],
          )}
        >
          {t.status}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-cabinet-cream truncate">{t.name}</p>
          <p className="text-[10px] text-cabinet-cream/50">
            Entry {formatDollars(t.entryFeeCents)} · {t.payoutStructure.length}{' '}
            paying place{t.payoutStructure.length === 1 ? '' : 's'}
          </p>
        </div>
      </Link>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
        <Link
          to={`/admin/tournament/${t.id}/manage`}
          className="text-xs text-cabinet-gold hover:text-cabinet-gold-bright px-2"
        >
          {canDelete ? 'Edit' : 'Manage'}
        </Link>
        {canDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onRequestDelete(t.id);
            }}
            className="text-xs text-cabinet-cream/40 hover:text-cabinet-flame-orange px-2"
          >
            Delete
          </button>
        )}
      </div>
      <span className="text-cabinet-cream/40">→</span>
    </li>
  );
}

function DeleteConfirm({
  name,
  pending,
  onConfirm,
  onCancel,
}: {
  name: string;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-5"
    >
      <div className="cabinet-card max-w-sm w-full p-5 space-y-3 border-cabinet-flame-orange/60">
        <h3 className="font-display text-cabinet-flame-orange text-lg uppercase tracking-widest">
          Delete this tournament?
        </h3>
        <p className="text-sm text-cabinet-cream/80">
          “{name}” will be permanently removed. This can't be undone.
        </p>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm text-cabinet-cream/60 hover:text-cabinet-cream px-3"
          >
            Cancel
          </button>
          <CasinoButton variant="red" onClick={onConfirm} disabled={pending}>
            {pending ? 'Deleting…' : 'Delete tournament'}
          </CasinoButton>
        </div>
      </div>
    </div>
  );
}
